'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getCookie, removeCookie, setCookie } from 'utils';

import { useTranslation } from '@/i18n/client';
import {
  addItemToBasket,
  BASKET_ID_COOKIE_NAME,
  changeItemQuantityInBasket,
  createBasketWithProduct,
  fetchMiniBasket,
  FreeShippingInformation,
  MiniBasket,
  removeItemFromBasket,
} from '@/modules/checkout/api';
import { useNotifications } from '@/shared/context/notifications-context';

/**
 * This context is global, used to render mini basket on all pages.
 * There is also `CheckoutContext` that is used only on routes from "checkout" group.
 *
 * `CheckoutContext` uses this context under the hood to perform actions. That way
 * we still have one source of truth when it comes to backend communications.
 *
 * Data fetching in `CheckoutContext` must be different
 * because backend is revalidating all products when it is hit.
 * Backend check for products and stock changes and updates the cart.
 * When changes are made, backend also sends `messages` which contain
 * information about these changes.
 */

export const BasketContext = createContext<{
  basketId: string | null;
  miniBasket: MiniBasket | null;
  isMiniBasketReloading: boolean;
  freeShippingPrice: { amount: number; currency: string };
  isCartSidebarOpen: boolean;
  setIsCartSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isCategorySidebarOpen: boolean;
  setIsCategorySidebarOpen: Dispatch<SetStateAction<boolean>>;
  canRenderCategorySidebarOpen: boolean;
  setCanRenderCategorySidebarOpen: Dispatch<SetStateAction<boolean>>;
  isLoginModalOpen: boolean;
  setIsLoginModalOpen: Dispatch<SetStateAction<boolean>>;
  addToBasket: (
    id: string,
    quantity?: number,
    ancillaryProducts?: string[]
  ) => Promise<string | null>;
  removeFromBasket: (id: string) => Promise<boolean>;
  changeBasketQuantity: (id: string, quantity: number) => Promise<boolean>;
  resetBasket: () => void;
}>({
  basketId: null,
  miniBasket: null,
  isMiniBasketReloading: false,
  freeShippingPrice: { amount: 0, currency: 'PLN' },
  isCartSidebarOpen: false,
  setIsCartSidebarOpen: () => {},
  canRenderCategorySidebarOpen: false,
  setCanRenderCategorySidebarOpen: () => {},
  isCategorySidebarOpen: false,
  isLoginModalOpen: false,
  setIsLoginModalOpen: () => {},
  setIsCategorySidebarOpen: () => {},
  addToBasket: () => Promise.resolve(null),
  removeFromBasket: () => Promise.resolve(false),
  changeBasketQuantity: () => Promise.resolve(false),
  resetBasket: () => {},
});

export const BasketProvider = ({
  children,
  basketId: initialBasketId = null,
  miniBasket: initialMiniBasketValue = null,
  freeShippingInformation: initialFreeShippingInformation = null,
}: {
  children: ReactNode;
  basketId?: string | null | undefined;
  miniBasket?: MiniBasket | null | undefined;
  freeShippingInformation: FreeShippingInformation | null | undefined;
}) => {
  const { t } = useTranslation(['common']);
  const { showNotification } = useNotifications();
  const [basketId, setBasketId] = useState<string | null>(initialBasketId);
  const [miniBasket, setMiniBasket] = useState<MiniBasket | null>(
    initialMiniBasketValue
  );
  const [isMiniBasketReloading, setIsMiniBasketReloading] =
    useState<boolean>(false);
  const [freeShippingPrice] = useState<{
    amount: number;
    currency: string;
  }>({
    amount: initialFreeShippingInformation?.price || 0,
    currency: initialFreeShippingInformation?.currency || 'PLN',
  });
  const [isCartSidebarOpen, setIsCartSidebarOpen] = useState<boolean>(false);
  const [isCategorySidebarOpen, setIsCategorySidebarOpen] =
    useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [canRenderCategorySidebarOpen, setCanRenderCategorySidebarOpen] =
    useState<boolean>(false);

  function updateCookieExpirationDate(basketIdToUpdate: string): void {
    const expires = new Date(new Date().setDate(new Date().getDate() + 14));
    setCookie(BASKET_ID_COOKIE_NAME, basketIdToUpdate, { expires });
  }

  const reloadMiniBasket = async (newBasketId: string) => {
    if (!newBasketId) return;

    setIsMiniBasketReloading(true);
    const result = await fetchMiniBasket(newBasketId);
    setMiniBasket(result);
    setIsMiniBasketReloading(false);
  };

  const addToBasket = async (
    productId: string,
    quantity: number = 1,
    ancillaryProducts: string[] = []
  ): Promise<string | null> => {
    async function createNewBasketAndAddProduct() {
      setIsMiniBasketReloading(true);

      const newBasketId = await createBasketWithProduct({
        productId,
        quantity,
      });

      if (!newBasketId) {
        setIsMiniBasketReloading(false);
        showNotification({
          id: 'add-to-basket-failed',
          type: 'error',
          message: t('common.addToCartFailed'),
        });
        return null;
      }

      const results: boolean[] = [];

      for (const ancillaryProductId of ancillaryProducts) {
        const res = await addItemToBasket({
          basketId: newBasketId,
          productId: ancillaryProductId,
          quantity: 1,
        });
        results.push(res);
      }

      setIsMiniBasketReloading(false);

      setBasketId(newBasketId);
      await reloadMiniBasket(newBasketId);

      if (results.includes(false)) {
        showNotification({
          id: 'add-ancillary-to-basket-failed',
          type: 'error',
          message: t('common.addAncillaryToCartFailed'),
        });
      }

      updateCookieExpirationDate(newBasketId);

      return newBasketId;
    }

    const currentBasketId = getCookie(BASKET_ID_COOKIE_NAME);
    setBasketId(currentBasketId);

    // Basket does not exist yet, create it.
    if (!currentBasketId) {
      return createNewBasketAndAddProduct();
    }

    setIsMiniBasketReloading(true);
    const result = await addItemToBasket({
      basketId: currentBasketId as string,
      productId,
      quantity,
    });

    if (result) {
      const results: boolean[] = [];

      for (const ancillaryProductId of ancillaryProducts) {
        const res = await addItemToBasket({
          basketId: currentBasketId as string,
          productId: ancillaryProductId,
          quantity: 1,
        });
        results.push(res);
      }

      setIsMiniBasketReloading(false);

      await reloadMiniBasket(currentBasketId as string);

      if (results.includes(false)) {
        showNotification({
          id: 'add-ancillary-to-basket-failed',
          type: 'error',
          message: t('common.addAncillaryToCartFailed'),
        });
      }

      updateCookieExpirationDate(currentBasketId as string);
      setTimeout(() => {
        document
          .querySelector(`#headerCart__product--${productId}`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
      }, 200);
    } else {
      // Basket is probably expired, try to create new one.
      if (!miniBasket?.items?.length && !result) {
        return createNewBasketAndAddProduct();
      }

      setIsMiniBasketReloading(false);

      showNotification({
        id: 'add-to-basket-failed',
        type: 'error',
        message: t('common.addToCartFailed'),
      });
    }

    return basketId;
  };

  const removeFromBasket = async (productId: string): Promise<boolean> => {
    if (!basketId) return false;

    setIsMiniBasketReloading(true);
    const result = await removeItemFromBasket({ basketId, productId });
    setIsMiniBasketReloading(false);
    if (result) {
      await reloadMiniBasket(basketId);
      updateCookieExpirationDate(basketId);
    }

    return result;
  };

  const changeBasketQuantity = async (
    productId: string,
    quantity: number
  ): Promise<boolean> => {
    if (!basketId) return false;

    setIsMiniBasketReloading(true);
    const result = await changeItemQuantityInBasket({
      basketId,
      productId,
      quantity,
    });
    setIsMiniBasketReloading(false);
    if (result) {
      await reloadMiniBasket(basketId);
      updateCookieExpirationDate(basketId);
    }

    return result;
  };

  const resetBasket = () => {
    setBasketId(null);
    setMiniBasket(null);
    setIsMiniBasketReloading(false);
  };

  useEffect(() => {
    if (miniBasket === null && getCookie(BASKET_ID_COOKIE_NAME)) {
      resetBasket();
      removeCookie(BASKET_ID_COOKIE_NAME);
    }
  }, [miniBasket]);

  return (
    <BasketContext.Provider
      value={{
        basketId,
        miniBasket,
        isMiniBasketReloading,
        freeShippingPrice,
        isCartSidebarOpen,
        isCategorySidebarOpen,
        isLoginModalOpen,
        setIsCartSidebarOpen,
        setIsCategorySidebarOpen,
        canRenderCategorySidebarOpen,
        setCanRenderCategorySidebarOpen,
        setIsLoginModalOpen,
        addToBasket,
        removeFromBasket,
        changeBasketQuantity,
        resetBasket,
      }}
    >
      {children}
    </BasketContext.Provider>
  );
};

export const useBasket = () => {
  return useContext(BasketContext);
};
